import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { StaffResponse } from '../../api/generated';
import type { RootState } from '../index';

interface StaffState {
  staffById: Record<string, StaffResponse>;
  // Which location's roster is in staffById, so a consumer can tell "this location has no staff"
  // from "not fetched yet" and knows to refetch after a location change. The roster is
  // location-scoped but the slice previously kept no record of whose it was.
  loadedLocationId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: StaffState = {
  staffById: {},
  loadedLocationId: null,
  loading: true,
  error: null
};

const staffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setStaff: (
      state,
      action: PayloadAction<{ staff: StaffResponse[]; locationId: string | null }>
    ) => {
      const staffById: Record<string, StaffResponse> = {};
      for (const s of action.payload.staff) {
        staffById[s.id] = s;
      }
      state.staffById = staffById;
      state.loadedLocationId = action.payload.locationId;
      state.loading = false;
      state.error = null;
    },
    upsertStaff: (state, action: PayloadAction<StaffResponse>) => {
      state.staffById[action.payload.id] = action.payload;
    },
    removeStaff: (state, action: PayloadAction<string>) => {
      delete state.staffById[action.payload];
    },
    clearStaff: (state) => {
      state.staffById = {};
      state.loadedLocationId = null;
      state.loading = false;
      state.error = null;
    },
    // Clearing the error is what re-arms the loader's effect, so this doubles as "retry".
    clearStaffError: (state) => {
      state.error = null;
    }
  }
});

export const {
  setLoading,
  setError,
  setStaff,
  upsertStaff,
  removeStaff,
  clearStaff,
  clearStaffError
} = staffSlice.actions;

// Memoized for the same reason as selectStaffCompetences: Object.values creates a new array
// per call, so an unmemoized selector hands every subscriber a fresh reference on each render.
// Anything deriving a memo from it (the patient drawer's preferred staff) would recompute
// forever and re-trigger effects that depend on the derived value.
export const selectStaff = createSelector(
  (state: RootState) => state.staffSlice.staffById,
  (staffById): StaffResponse[] => Object.values(staffById)
);

export const selectStaffLoadedLocationId = (state: RootState): string | null =>
  state.staffSlice.loadedLocationId;

export const selectStaffLoading = (state: RootState): boolean => state.staffSlice.loading;

export const selectStaffError = (state: RootState): string | null => state.staffSlice.error;

export default staffSlice.reducer;
